import React, { useState, useEffect, useContext } from 'react';
import { useMediaQuery } from 'react-responsive';
import ReactCountryFlag from "react-country-flag";
import "./otherHeader.css";
import "../../styles/fonts.css";
import {
  getUserInfoService,
  getUserPhotoService,
} from '../../../infrastructure/services/UserInfoService';
import { getAnotherUserInfoService } from '../../../infrastructure/services/UsersService';
import { getMessagesService } from '../../../infrastructure/services/NotificationsService';
import { getInteractionPermissionsService } from '../../../infrastructure/services/PublicationService';
import { FollowActions } from '../follows/FollowActions';
import { FollowCount } from '../layout/FollowCount';
import { LangContext, UserDataContext } from '../../contexts/Contexts';
import {
  DEFAULT_PROFILE_IMAGE,
  normalizeImageSrc,
} from '../../../infrastructure/utils/normalizeImageSrc';
import { USER_CONNECTIONS_CHANGED_EVENT } from '../../../infrastructure/hooks/useGetFollowers';

const INTERACTION_PERMISSIONS_EVENT = "vivanomada:interaction-permissions";

// Fallback: si permisos-interaccion no trae canSendMessage, usamos el mismo
// origen que el chat. "privacidadMensaje": 1 = Privado (Solo yo).
const MESSAGE_PRIVACY_PRIVATE = 1;

const getResponsePayload = (response) => response?.data ?? response ?? {};

const getNumberFromPayload = (payload, keys = []) => {
  for (const key of keys) {
    const value = payload?.[key];
    if (value !== undefined && value !== null && value !== "") {
      const numberValue = Number(value);
      return Number.isFinite(numberValue) ? numberValue : 0;
    }
  }

  return 0;
};

const getTextFromPayload = (payload, keys = []) => {
  for (const key of keys) {
    const value = payload?.[key];
    if (value !== undefined && value !== null && String(value).trim()) {
      return String(value).trim();
    }
  }

  return "";
};

const getDisplayUser = (user = {}) => {
  const firstName = getTextFromPayload(user, [
    "nombres",
    "Nombres",
    "primerNombre",
    "PrimerNombre",
    "nombre",
    "Nombre",
    "name",
    "Name",
  ]);
  const lastName = getTextFromPayload(user, [
    "apellidos",
    "Apellidos",
    "apellido",
    "Apellido",
    "apellidoPaterno",
    "ApellidoPaterno",
    "lastName",
    "LastName",
  ]);
  const fullName =
    getTextFromPayload(user, ["nombreCompleto", "NombreCompleto", "fullName"]) ||
    [firstName, lastName].filter(Boolean).join(" ");
  const nickName = getTextFromPayload(user, [
    "nickName",
    "NickName",
    "nickname",
    "Nickname",
    "usuario",
    "Usuario",
  ]);

  return { fullName, nickName };
};

const getCanSendMessageFromPermissions = (permissions) => {
  const value = permissions?.canSendMessage ?? permissions?.CanSendMessage;
  return typeof value === "boolean" ? value : null;
};

const findCanSendMessageFromConversations = (conversations = [], targetUserId) => {
  const match = (conversations || []).find(
    (conversation) =>
      String(
        conversation?.idUsuarioOrigen ??
          conversation?.idUsuario ??
          conversation?.id ??
          "",
      ) === String(targetUserId ?? ""),
  );

  if (!match) {
    return null;
  }

  const value =
    match.puedeMandarMensaje ??
    match.PuedeMandarMensaje ??
    match.puedeVer ??
    match.PuedeVer;

  return value === false ? false : true;
};

export const OtherUserHeaderProfile = ({ user, relation, uiTexts }) => {
  
  const [userPhoto, setUserPhoto] = useState(DEFAULT_PROFILE_IMAGE);
  const [profileUser, setProfileUser] = useState(user || {});
  const [followers, setFollowers] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [canSendMessage, setCanSendMessage] = useState(null);
  const { userData } = useContext(UserDataContext);
  const { lang } = useContext(LangContext);
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const isSpanish = String(lang || "en").toLowerCase().startsWith("es");
  const displayUser = getDisplayUser(profileUser);
  
  useEffect(() => {
    if (user?.idUsuario) {
      setProfileUser(user);
      setUserPhoto(normalizeImageSrc(user?.fotoPerfilUrl) || DEFAULT_PROFILE_IMAGE);
      getProfilePicture();
      getUserInfo();
    }
  }, [user]);

  useEffect(() => {
    if (!user?.idUsuario) return;

    getProfilePicture();
    getUserInfo();
  }, [user?.idUsuario, userData?.idUsuario]);

  useEffect(() => {
    setCanSendMessage(null);

    if (!userData?.idUsuario || !user?.idUsuario) {
      setCanSendMessage(true);
      return;
    }

    if (String(userData.idUsuario) === String(user.idUsuario)) {
      setCanSendMessage(false);
      return;
    }

    let isMounted = true;

    const resolveMessagePermission = async () => {
      try {
        try {
          const interactionPermissions = await getInteractionPermissionsService({
            userGuid: userData.idUsuario,
            targetUserGuid: user.idUsuario,
          });

          if (!isMounted) return;

          const permissionCanSendMessage =
            getCanSendMessageFromPermissions(interactionPermissions);

          if (permissionCanSendMessage !== null) {
            setCanSendMessage(permissionCanSendMessage);
            return;
          }
        } catch (permissionsError) {
          console.error("Error resolving interaction permissions", permissionsError);
        }

        const conversations = await getMessagesService(userData.idUsuario);
        if (!isMounted) return;

        const result = findCanSendMessageFromConversations(
          conversations,
          user.idUsuario,
        );

        if (result !== null) {
          setCanSendMessage(result);
          return;
        }

        setCanSendMessage(true);
      } catch (error) {
        console.error("Error resolving message permission", error);
        if (isMounted) {
          setCanSendMessage(true);
        }
      }
    };

    resolveMessagePermission();

    return () => {
      isMounted = false;
    };
  }, [user?.idUsuario, userData?.idUsuario]);

  useEffect(() => {
    if (!user?.idUsuario) return undefined;

    const handleConnectionsChanged = (event) => {
      const detail = event?.detail || {};
      const affectedIds = [
        detail.idUsuario,
        detail.IdUsuario,
        detail.idAmigo,
        detail.IdAmigo,
      ].map((id) => String(id ?? ""));

      if (affectedIds.includes(String(user.idUsuario))) {
        getUserInfo();
      }
    };

    window.addEventListener(USER_CONNECTIONS_CHANGED_EVENT, handleConnectionsChanged);

    return () => {
      window.removeEventListener(
        USER_CONNECTIONS_CHANGED_EVENT,
        handleConnectionsChanged,
      );
    };
  }, [user?.idUsuario, userData?.idUsuario]);

  useEffect(() => {
    if (!userData?.idUsuario || !user?.idUsuario) {
      return undefined;
    }

    const handleInteractionPermissions = (event) => {
      const detail = event?.detail || {};

      if (
        String(detail.userGuid ?? "") !== String(userData.idUsuario) ||
        String(detail.targetUserGuid ?? "") !== String(user.idUsuario)
      ) {
        return;
      }

      const nextCanSendMessage =
        getCanSendMessageFromPermissions(detail.permissions);
      if (nextCanSendMessage !== null) {
        setCanSendMessage(nextCanSendMessage);
      }
    };

    window.addEventListener(
      INTERACTION_PERMISSIONS_EVENT,
      handleInteractionPermissions,
    );

    return () => {
      window.removeEventListener(
        INTERACTION_PERMISSIONS_EVENT,
        handleInteractionPermissions,
      );
    };
  }, [user?.idUsuario, userData?.idUsuario]);

  const getProfilePicture = async () => {
    try {
      const req = { IdUsuario: user?.idUsuario };
      const photo = await getUserPhotoService(req);
      const nextPhoto =
        normalizeImageSrc(photo?.content) ||
        normalizeImageSrc(photo?.fotoPerfilUrl) ||
        normalizeImageSrc(user?.fotoPerfilUrl) ||
        normalizeImageSrc(user?.imagenBase64) ||
        normalizeImageSrc(user?.fotoPerfilBase64) ||
        normalizeImageSrc(user?.profPic) ||
        DEFAULT_PROFILE_IMAGE;

      setUserPhoto(nextPhoto);

      if (nextPhoto !== DEFAULT_PROFILE_IMAGE) {
        sessionStorage.setItem("OtherUserPP", nextPhoto);
      }
    } catch (error) {
      console.error("Error getting user photo", error);
      setUserPhoto(
        normalizeImageSrc(user?.fotoPerfilUrl) ||
          normalizeImageSrc(user?.imagenBase64) ||
          normalizeImageSrc(user?.fotoPerfilBase64) ||
          normalizeImageSrc(user?.profPic) ||
          DEFAULT_PROFILE_IMAGE,
      );
    }
  };

  // Función para obtener la cantidad de seguidores y seguidos
  const getUserInfo = async () => {
    let shouldLoadFullProfile = !getDisplayUser(user).fullName;

    try {
      const info = await getAnotherUserInfoService(userData?.idUsuario, user?.idUsuario);
      const payload = getResponsePayload(info);

      if (payload) {
        setProfileUser((current) => ({
          ...current,
          ...payload,
          idUsuario: current?.idUsuario || payload?.idUsuario || payload?.IdUsuario,
        }));
        setFollowers(
          getNumberFromPayload(payload, [
            "TotalMeSiguen",
            "totalMeSiguen",
            "total_me_siguen",
            "TotalSeguidores",
            "totalSeguidores",
            "followers",
            "seguidores",
          ]),
        );
        setFollowingCount(
          getNumberFromPayload(payload, [
            "TotalSigo",
            "totalSigo",
            "total_sigo",
            "TotalSeguidos",
            "totalSeguidos",
            "following",
            "seguidos",
            "siguiendo",
          ]),
        );
      }

      const nextDisplayUser = getDisplayUser({ ...user, ...payload });
      shouldLoadFullProfile = !nextDisplayUser.fullName;
    } catch (error) {
      console.error("Error getting user info", error);
    }

    if (shouldLoadFullProfile && user?.idUsuario) {
      try {
        const fullInfo = await getUserInfoService(user.idUsuario);
        const fullPayload = getResponsePayload(fullInfo);
        setProfileUser((current) => ({
          ...current,
          ...fullPayload,
          idUsuario:
            current?.idUsuario ||
            fullPayload?.idUsuario ||
            fullPayload?.IdUsuario ||
            user.idUsuario,
        }));
      } catch (error) {
        console.error("Error getting full user profile", error);
      }
    }
  };

  return (
  
      <div style={{ width: "100%", display: "flex", justifyContent: "flex-end"}}>
        <div
          style={{
            width: "100%",
            backgroundImage: "url('/img/bg-images/desktop/profile-bg.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            zIndex: "1",
            padding: isMobile ? "1rem" : "2rem",
            
          }}
        >
          <div style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: "1rem",
              alignItems: "center",
              justifyContent: isMobile ? "center" : "space-between"
            }}>
            <div style={{ 
                display: "flex", 
                flexDirection: isMobile ? "column" : "row", 
                gap: "1rem", 
                alignItems: "center" 
              }}>
              <div className="user-header-profile-container other-user-header-profile-container center-elements">
                <img
                  src={userPhoto || DEFAULT_PROFILE_IMAGE}
                  alt="profile-pic"
                  className="profile-img-sidebar"
                />
              </div>
              <div style={{ color: "white", textAlign: isMobile ? "center" : "left" }}>
                <p style={{
                    fontSize: isMobile ? "18px" : "20px",
                    fontWeight: "bold",
                    padding: "0",
                    margin: "0"
                  }}>
                  {displayUser.fullName || displayUser.nickName}
                  {(profileUser?.idNacionalidad || profileUser?.IdNacionalidad) && (
                    <ReactCountryFlag
                      className="center-elements"
                      style={{ marginLeft: "5px" }}
                      countryCode={profileUser?.idNacionalidad || profileUser?.IdNacionalidad}
                      svg
                    />
                  )}
                </p>
                {displayUser.nickName && displayUser.nickName !== displayUser.fullName && (
                  <p style={{ fontSize: "14px", padding: "0", margin: "0" }}>
                    {displayUser.nickName}
                  </p>
                )}
                <div style={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: "center",
                    marginTop: "0.5rem",
                    gap: "1rem",
                    justifyContent: "center"
                  }}>
                  <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem"
                    }}>
                    <div
                    className='dot-online-other-user-chat'
                    />
                    {/* <span style={{ fontSize: "14px" }}>Status</span> */}
                  </div>
                  <div style={{ display: "flex", gap: "1rem", color: "black" }}>
                    <FollowCount
                      count={followers}
                      text={isSpanish ? "seguidores" : "followers"}
                    />
                    <FollowCount
                      count={followingCount}
                      text={isSpanish ? "siguiendo" : "following"}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div style={!isMobile ? { display: "flex" } : { display: "flex", flexDirection: "row" }}>
              <FollowActions
                user={user}
                profPic={userPhoto || DEFAULT_PROFILE_IMAGE}
                relation={relation}
                canSendMessage={canSendMessage}
              />
            </div>
          </div>
        </div>
      </div>
    
  );
};
